import axios from 'axios';
import { route } from 'ziggy-js';

const availabilityRequestKey = (data) =>
    [
        data.eventTypeExternalId,
        data.startTime.toISOString(),
        data.endTime.toISOString(),
        data.participants,
        data.quantity,
    ].join('|');

export default {
    // Set vendor
    setPage(state, payload) {
        state.page.title = payload.title;
        state.page.vpv = payload.vpv;
    },
    setSelectedLanguage(state, payload) {
        state.selectedLanguage = payload;
    },

    // Set vendor
    setVendor(state, payload) {
        state.vendor = payload;
        state.settings.showPricesWithVat = !!state.vendor.vendor_settings.show_prices_with_vat;
    },
    setVersion(state, version) {
        state.version = version;
    },

    setRatePlan(state, payload) {
        state.ratePlan = payload;
    },

    // Set event type
    setEventTypes(state, payload) {
        state.eventTypes = payload;
    },
    setEventSearchCanInstantBook(state, payload) {
        state.instantBooking.searchCanInstantBook = payload;
    },

    // Set event type
    setEventType(state, payload) {
        state.eventType = payload;
        state.settings.showPriceCalendar = !!state.eventType.price_calendar_active;
        state.settings.showInstantBooking = !!state.eventType.instant_booking_active;
        state.settings.showAvailabilityInCalendar = !!state.eventType.availability_calendar;
    },

    // Set packages
    setPackages(state, payload) {
        state.packages = Array.isArray(payload) ? payload : [];
    },

    // Set package
    setPackage(state, payload) {
        state.package = payload;
    },

    // Set themes
    setThemes(state, payload) {
        state.themes = payload;
    },

    // Set classes
    setClasses(state, payload) {
        state.classes = payload;
    },

    setSimpleOrder(state) {
        state.order.vendor = state.vendor;
        state.order.package = state.package;
        state.order.startTime = state.search.startTime;
        state.order.endTime = state.search.endTime;
        state.order.participants = state.search.participants;
    },

    // Set search
    setSearch(state, payload) {
        state.search.participants = payload.participants;
        state.search.startTime = payload.startTime;
        state.search.endTime = payload.endTime;
        state.search.accommodation = payload.accommodation;
        state.search.agreementCode = payload.agreementCode;
        state.search.currencyCode = payload.currencyCode;
        if (
            state.search.participants !== null &&
            state.search.startTime !== null &&
            state.search.endTime !== null &&
            state.search.accommodation !== null
        ) {
            state.search.complete = true;
        }
    },
    unsetSearchCompleted(state) {
        state.search.complete = false;
    },

    // Set packages loading
    setPackagesLoading(state) {
        state.loading.packages = true;
    },
    unsetPackagesLoading(state) {
        state.loading.packages = false;
    },

    // Set price calendar prices
    setPriceCalendarLoading(state) {
        state.loading.priceCalendar = true;
    },
    unsetPriceCalendarLoading(state) {
        state.loading.priceCalendar = false;
    },

    // Fetch price calendar prices
    resetPriceCalendar(state) {
        if (state.settings.showPriceCalendar) {
            state.priceCalendar.prices = null;
            state.priceCalendar.pricesVat = null;
            state.priceCalendar.bestPriceDates = null;
            state.priceCalendar.alreadyFetched = {};
            state.priceCalendar.scrolledPast = [];
        }
    },
    pushScrolledPast(state, payload) {
        state.priceCalendar.scrolledPast.push(payload);
    },
    fetchPriceCalendarPrices(state, payload) {
        if (state.settings.showPriceCalendar) {
            state.loading.priceCalendar = true;
            let data = {
                event_type_id: state.eventType.id,
                startTime: state.search.startTime,
                endTime: state.search.endTime,
                participants: state.search.participants,
                accommodation: state.search.accommodation,
                agreementCode: state.search.agreementCode,
                currencyCode: state.search.currencyCode,
                year: payload.y,
                month: payload.m,
                package_id: state.package ? state.package.id : null,
            };
            if (
                !state.priceCalendar.alreadyFetched[payload.y] ||
                !state.priceCalendar.alreadyFetched[payload.y].includes(payload.m)
            ) {
                axios
                    .post(route('tenant.api.price-calendar'), data)
                    .then((response) => {
                        if (response.data.pricesFound) {
                            if (!state.priceCalendar.prices) {
                                state.priceCalendar.prices = response.data.prices;
                                state.priceCalendar.pricesVat = response.data.pricesVat;
                                state.priceCalendar.bestPriceDates = response.data.bestPriceDates;
                            } else {
                                state.priceCalendar.prices = { ...state.priceCalendar.prices, ...response.data.prices };
                                state.priceCalendar.pricesVat = {
                                    ...state.priceCalendar.pricesVat,
                                    ...response.data.pricesVat,
                                };
                                state.priceCalendar.bestPriceDates = state.priceCalendar.bestPriceDates.concat(
                                    response.data.bestPriceDates
                                );
                            }
                            if (!state.priceCalendar.alreadyFetched[payload.y]) {
                                state.priceCalendar.alreadyFetched[payload.y] = [];
                            }
                            state.priceCalendar.alreadyFetched[payload.y].push(payload.m);
                        } else {
                            this.resetPriceCalendar();
                            this.unsetPriceCalendarLoading();
                        }
                    })
                    .catch(() => {
                        state.loading.priceCalendar = false;
                    });
            }
        }
    },
    fetchInstantBookingForSearchPeriod(state) {
        if (state.settings.showInstantBooking || state.settings.showAvailabilityInCalendar) {
            const now = new Date();

            let data = {
                eventTypeExternalId: state.eventType.external_id,
                startTime: state.search.startTime ?? now,
                endTime: state.search.endTime ?? now,
                participants: state.search.participants ?? 10,
                quantity: state.search.participants ?? 10, // TODO: quantity assumes 1 room per participant, replace with actual room count when the booking UI captures it
            };
            axios
                .post(route('tenant.api.availability-calendar-per-day'), data)
                .then((response) => {
                    const spaces = response.data.spaces;
                    const rooms = response.data.rooms;
                    state.availabilityCalendarRooms.specificDateSearch = rooms;
                    state.availabilityCalendarSpaces.specificDateSearch = spaces;
                })
                .catch(() => {
                    state.loading.availabilityCalendarRooms = false;
                    state.loading.availabilityCalendarSpaces = false;
                });
        }
    },
    fetchCanEventInstantBook(state) {
        state.instantBooking.searchCanInstantBook = false;
        if (state.settings.showInstantBooking) {
            let data = {
                eventTypeExternalId: state.eventType.external_id,
                startTime: state.search.startTime,
                endTime: state.search.endTime,
                participants: state.search.participants,
                quantity: state.search.participants, // TODO: quantity assumes 1 room per participant, replace with actual room count when the booking UI captures it
                accommodation: state.search.accommodation,
            };
            axios
                .post(route('tenant.api.availability-check'), data)
                .then((response) => {
                    state.instantBooking.searchCanInstantBook = response.data;
                })
                .catch(() => {
                    state.instantBooking.searchCanInstantBook = false;
                });
        }
    },
    startPackageInstantBookCheck(state) {
        state.instantBooking.packageCheckSequence += 1;
    },
    setPackageCanInstantBook(state, payload) {
        state.instantBooking.packageCanInstantBook = payload;
    },
    fetchInstantBookingDays(state, payload) {
        let startDate = new Date(state.search.startTime);
        let endDate = new Date(state.search.endTime);

        let startHour = startDate.getUTCHours();
        let startMinute = startDate.getUTCMinutes();

        let endHour = endDate.getUTCHours();
        let endMinute = endDate.getUTCMinutes();

        const month = payload.m;
        const year = payload.y;

        const firstDayOfMonth = new Date(Date.UTC(year, month, 1, startHour, startMinute));
        const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0, endHour, endMinute));

        let data = {
            eventTypeExternalId: state.eventType.external_id,
            startTime: firstDayOfMonth,
            endTime: lastDayOfMonth,
            participants: state.search.participants ?? 10,
            quantity: state.search.participants ?? 10, // TODO: quantity assumes 1 room per participant, replace with actual room count when the booking UI captures it
        };

        if (state.settings.showInstantBooking || state.settings.showAvailabilityInCalendar) {
            const requestKey = availabilityRequestKey(data);

            if (
                state.availabilityCalendarRequests.pending[requestKey] ||
                state.availabilityCalendarRequests.fetched[requestKey]
            ) {
                return;
            }

            state.availabilityCalendarRequests.pending[requestKey] = true;

            return axios
                .post(route('tenant.api.availability-calendar-per-day'), data)
                .then((response) => {
                    const spaces = response.data.spaces;
                    const rooms = response.data.rooms;
                    state.availabilityCalendarSpaces.dates = { ...state.availabilityCalendarSpaces.dates, ...spaces };
                    state.availabilityCalendarRooms.dates = { ...state.availabilityCalendarRooms.dates, ...rooms };
                    state.availabilityCalendarRequests.fetched[requestKey] = true;
                })
                .catch(() => {
                    state.loading.availabilityCalendarRooms = false;
                    state.loading.availabilityCalendarSpaces = false;
                })
                .finally(() => {
                    delete state.availabilityCalendarRequests.pending[requestKey];
                });
        }
    },

    // Toggle show with vat
    toggleVat(state) {
        state.settings.showPricesWithVat = !state.settings.showPricesWithVat;
    },
};
